import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../../services/api';

export const PAYER_LABELS = {
  ODM: 'ODM Fee-for-Service',
  DODD: 'DODD',
  ODA: 'ODA (Aging)',
  ManagedCare: 'Managed Care',
  MyCare: 'MyCare Ohio',
};

export const PAYER_ORDER = ['ODM', 'DODD', 'ManagedCare', 'ODA', 'MyCare'];

const blankContractForm = () => ({
  contract_name: '',
  start_date: new Date().toISOString().split('T')[0],
  end_date: '',
});

const blankRateForm = () => ({
  hcpcs_code: '',
  description: '',
  unit_type: '15min',
  unit_minutes: 15,
  unit_rate: 0,
  base_rate: 0,
  modifiers: [],
});

/**
 * Centralises every API call + transient state for the Payer Contracts page.
 * Returns the data, the dialog flags, and bound action handlers.
 */
export function usePayerContracts() {
  const [contractsByPayer, setContractsByPayer] = useState({});
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [showNewContract, setShowNewContract] = useState(false);
  const [newContractPayer, setNewContractPayer] = useState('');
  const [newContractForm, setNewContractForm] = useState(blankContractForm);
  const [savingContract, setSavingContract] = useState(false);
  const [editingRate, setEditingRate] = useState(null);
  const [savingRate, setSavingRate] = useState(false);
  const [addRateDialog, setAddRateDialog] = useState(null);
  const [newRate, setNewRate] = useState(blankRateForm);

  const fetchContracts = useCallback(async () => {
    try {
      const res = await api.get('/payer-contracts/by-payer');
      setContractsByPayer(res.data);
    } catch {
      toast.error('Failed to load contracts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchContracts(); }, [fetchContracts]);

  const seedDefaults = async () => {
    setSeeding(true);
    try {
      const res = await api.post('/payer-contracts/seed-defaults');
      toast.success(res.data.message);
      fetchContracts();
    } catch {
      toast.error('Failed to seed defaults');
    } finally {
      setSeeding(false);
    }
  };

  const createContract = async () => {
    if (!newContractPayer || !newContractForm.contract_name) {
      toast.error('Payer and contract name required');
      return;
    }
    setSavingContract(true);
    try {
      await api.post('/payer-contracts', { payer_type: newContractPayer, ...newContractForm });
      toast.success('Contract created with Ohio standard rates');
      setShowNewContract(false);
      setNewContractForm(blankContractForm());
      setNewContractPayer('');
      fetchContracts();
    } catch {
      toast.error('Failed to create contract');
    } finally {
      setSavingContract(false);
    }
  };

  const deleteContract = async (contractId) => {
    if (!window.confirm('Delete this contract and all its rates?')) return;
    try {
      await api.delete(`/payer-contracts/${contractId}`);
      toast.success('Contract deleted');
      fetchContracts();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const toggleContractStatus = async (contract) => {
    const newStatus = contract.status === 'Active' ? 'Expired' : 'Active';
    try {
      await api.put(`/payer-contracts/${contract.id}`, { status: newStatus });
      toast.success(`Contract ${newStatus === 'Active' ? 'activated' : 'expired'}`);
      fetchContracts();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const startEditRate = (contractId, rate) =>
    setEditingRate({ contractId, hcpcs_code: rate.hcpcs_code, unit_rate: rate.unit_rate, base_rate: rate.base_rate || 0 });

  const saveRate = async () => {
    if (!editingRate) return;
    setSavingRate(true);
    try {
      await api.put(
        `/payer-contracts/${editingRate.contractId}/rates/${editingRate.hcpcs_code}?unit_rate=${editingRate.unit_rate}&base_rate=${editingRate.base_rate}`
      );
      toast.success(`${editingRate.hcpcs_code} rate updated`);
      setEditingRate(null);
      fetchContracts();
    } catch {
      toast.error('Failed to update rate');
    } finally {
      setSavingRate(false);
    }
  };

  const addRate = async () => {
    if (!addRateDialog || !newRate.hcpcs_code) return;
    try {
      await api.post(`/payer-contracts/${addRateDialog}/rates`, newRate);
      toast.success(`${newRate.hcpcs_code} added`);
      setAddRateDialog(null);
      setNewRate(blankRateForm());
      fetchContracts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add rate');
    }
  };

  const deleteRate = async (contractId, hcpcsCode) => {
    if (!window.confirm(`Remove ${hcpcsCode} from this contract?`)) return;
    try {
      await api.delete(`/payer-contracts/${contractId}/rates/${hcpcsCode}`);
      toast.success(`${hcpcsCode} removed`);
      fetchContracts();
    } catch {
      toast.error('Failed to remove rate');
    }
  };

  const hasContracts = Object.values(contractsByPayer).some((c) => c.length > 0);

  return {
    contractsByPayer, loading, hasContracts,
    seeding, seedDefaults,
    showNewContract, setShowNewContract,
    newContractPayer, setNewContractPayer,
    newContractForm, setNewContractForm,
    savingContract, createContract,
    deleteContract, toggleContractStatus,
    editingRate, setEditingRate,
    savingRate, startEditRate, saveRate,
    addRateDialog, setAddRateDialog,
    newRate, setNewRate, addRate, deleteRate,
  };
}
